import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { House, ChartLineUp, Factory, GearSix } from "@phosphor-icons/react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import CommandPalette, { useCommandPalette } from "@/components/ui/CommandPalette";
import DemoBanner from "@/demo/DemoBanner";
import { DEMO } from "@/demo/demo";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DossierDetail = lazy(() => import("@/pages/DossierDetail"));
const ReportView = lazy(() => import("@/pages/ReportView"));
const Accuracy = lazy(() => import("@/pages/Accuracy"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Admin = lazy(() => import("@/pages/Admin"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const Verify = lazy(() => import("@/pages/Verify"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Global Cmd+K command palette. Rendered inside the router so navigation works,
 *  and inside AuthProvider so it can role-gate TPI-only destinations. */
function GlobalCommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const { user } = useAuth();
  const isTpi = user?.role === "tpi_inspector";

  const navItems = [
    { id: "nav-dashboard", label: "Dashboard", description: "All dossiers", icon: House, onSelect: "/" },
    { id: "nav-accuracy", label: "Accuracy", description: "Verification metrics", icon: ChartLineUp, onSelect: "/accuracy" },
    ...(isTpi
      ? [
          { id: "nav-suppliers", label: "Suppliers", description: "Supplier registry", icon: Factory, onSelect: "/suppliers" },
          { id: "nav-admin", label: "Admin Console", description: "Users, standards, audit log", icon: GearSix, onSelect: "/admin" },
        ]
      : []),
  ];

  return (
    <CommandPalette
      open={open}
      onOpenChange={setOpen}
      groups={[{ heading: "Navigate", items: navItems }]}
    />
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen text-zinc-400 text-sm">Loading…</div>}>
      <Routes>
        <Route path="/verify" element={<Verify />} />
        <Route path="/login" element={DEMO ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/dossier/:id" element={<ProtectedRoute><DossierDetail /></ProtectedRoute>} />
        <Route path="/dossier/:id/report" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
        <Route path="/accuracy" element={<ProtectedRoute><Accuracy /></ProtectedRoute>} />
        <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <DemoBanner />
          <Toaster position="top-right" richColors />
          <GlobalCommandPalette />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
